import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../features/auth/context/AuthContext';
import { showToast } from '../shared/utils/core/fetchWrapper';
import PWAHandler from '../shared/components/pwa/PWAHandler';
import BottomNavBar from '../shared/components/ui/BottomNavBar';
import { FloatingInsight } from '../features/insights/components/InvisibleInsight';
import SearchModal from '../shared/components/search/SearchModal';
import { SwipeablePages } from '../shared/components/mobile/SwipeableTabs';
import { useKeyboardShortcut } from '../shared/hooks/useKeyboardShortcut';
import { useNotifications } from '../shared/hooks/useNotifications';
import { useAppSession } from '../shared/hooks/useAppSession';
import { isNative } from '../shared/utils/core/platform';
import { useOnboarding } from '../features/onboarding/context/OnboardingContext';
import Onboarding from '../features/onboarding/components/Onboarding';
import EmailVerificationScreen from '../features/auth/components/EmailVerificationScreen';
export default function Layout() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [verificationSkipped, setVerificationSkipped] = useState(false);
  const { user, loading, authInitialized, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize notification system (FCM, activity tracking, etc.)
  useNotifications();

  // Track app session duration (open → close/background)
  useAppSession(user?.uid);

  // Onboarding flow
  const {
    shouldShowOnboarding,
    isChecking: isCheckingOnboarding,
    markOnboardingComplete,
    skipOnboarding
  } = useOnboarding();

  // Ctrl+N = New journal entry
  useKeyboardShortcut('n', () => navigate('/journal/new'), {
    ctrl: true,
    enabled: user && !searchOpen
  });

  // Keyboard shortcut for search (Ctrl/Cmd+K)
  useKeyboardShortcut('k', () => setSearchOpen(true), {
    ctrl: true,
    preventDefault: true,
    enabled: user && !searchOpen
  });

  // Listen for search modal event from BottomNavBar
  useEffect(() => {
    const handleOpenSearch = () => setSearchOpen(true);
    window.addEventListener('open-search-modal', handleOpenSearch);
    return () => window.removeEventListener('open-search-modal', handleOpenSearch);
  }, []);

  // Handle deep links on web (query params on initial load or navigation)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('quickCapture') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      navigate('/journal/new');
    }
    // Email verification link lands on /profile?verified=true — show toast and clear param
    if (urlParams.get('verified') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      refreshUser().then(() => {
        showToast('Email verified! Welcome to Clarity.', 'success');
      }).catch(() => {});
    }
  }, [location]);

  // Native deep link listener via Capacitor App plugin — register once
  // Handles Universal Links from email verification and push notification taps
  useEffect(() => {
    if (!isNative()) return;
    let listenerHandle;
    import('@capacitor/app').then(({ App }) => {
      App.addListener('appUrlOpen', (data) => {
        try {
          const url = new URL(data.url);
          if (url.searchParams.get('quickCapture') === 'true') {
            navigate('/journal/new');
          } else if (url.searchParams.get('verified') === 'true') {
            // Universal Link from email verification — refresh auth and show toast
            refreshUser().then(() => {
              showToast('Email verified! Welcome to Clarity.', 'success');
              navigate('/profile', { replace: true });
            }).catch(() => {
              navigate('/profile', { replace: true });
            });
          }
        } catch (e) {}
      }).then(handle => { listenerHandle = handle; });
    }).catch(() => {});
    return () => { listenerHandle?.remove(); };
  }, []);

  // Native: configure status bar and hide splash screen
  useEffect(() => {
    if (!isNative()) return;
    (async () => {
      try {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#1a1410' });
      } catch (e) {}
      try {
        const { SplashScreen } = await import('@capacitor/splash-screen');
        await SplashScreen.hide();
      } catch (e) { console.warn('[Layout] SplashScreen.hide failed:', e); }
    })();
  }, []);

  // Listen for paywall navigation events (dispatched by SubscriptionContext.openPaywall)
  useEffect(() => {
    const handlePaywall = () => navigate('/paywall');
    window.addEventListener('navigate-to-paywall', handlePaywall);
    return () => window.removeEventListener('navigate-to-paywall', handlePaywall);
  }, [navigate]);

  // Listen for subscription manage navigation events (dispatched by SubscriptionContext.presentCustomerCenter on web)
  useEffect(() => {
    const handleManage = () => navigate('/subscription/manage');
    window.addEventListener('navigate-to-subscription-manage', handleManage);
    return () => window.removeEventListener('navigate-to-subscription-manage', handleManage);
  }, [navigate]);

  // Listen for FCM notification click navigation (works in web + Capacitor native).
  // Also drain any cold-start navigation that was queued before this listener mounted.
  useEffect(() => {
    const handleNav = (e) => navigate(e.detail);
    window.addEventListener('clarity-navigate', handleNav);

    // Drain cold-start queue: nativePush.js may have fired before React mounted.
    // Only consume if recent (within 10s) to avoid replaying stale taps.
    const pending = window.__clarityPendingNav;
    if (pending && Date.now() - pending.ts < 10000) {
      navigate(pending.url);
    }
    window.__clarityPendingNav = null;

    return () => window.removeEventListener('clarity-navigate', handleNav);
  }, [navigate]);

  // Reset verification skip state whenever the user identity changes (including direct user→user)
  useEffect(() => {
    setVerificationSkipped(false);
  }, [user?.uid]);


  // Show loading state while auth is initializing
  // Wait for BOTH loading to complete AND auth to be fully initialized
  if (loading || !authInitialized) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-[var(--theme-background)]">
        <div className="text-center space-y-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--theme-primary)]"></div>
          <p className="text-[var(--theme-foreground)]">Loading...</p>
        </div>
      </div>
    );
  }

  // Handle authentication-based routing
  const publicRoutes = ['/login', '/legal/privacy', '/legal/terms', '/auth/action', '/shared/'];
  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));

  // Redirect authenticated users away from login page
  if (user && location.pathname === '/login') {
    return <Navigate to="/profile" replace />;
  }

  // Redirect unauthenticated users to login (except for public routes)
  if (!user && !isPublicRoute) {
    return <Navigate to="/login" replace />;
  }

  // Don't show main layout on login page or auth action page
  if (location.pathname === '/login' || location.pathname.startsWith('/auth/action')) {
    return <Outlet />;
  }

  const verificationWhitelist = ['eric@clarity.co'];
  const isWhitelisted = user?.email && verificationWhitelist.includes(user.email.toLowerCase());

  // 1. ONBOARDING — shown first, before email verification, so new users experience
  //    the app before hitting an email-check wall.
  if (user && !isCheckingOnboarding && shouldShowOnboarding) {
    return (
      <div className="min-h-dvh w-full bg-gradient-to-br from-[#0a0806] via-[#1a1410] to-[#0a0806]">
        <Onboarding
          onComplete={(options) => markOnboardingComplete(options)}
          onSkip={(options) => skipOnboarding(options)}
        />
      </div>
    );
  }

  // 2. EMAIL VERIFICATION — shown after onboarding completes/skips.
  //    Google OAuth users are pre-verified so this only applies to email signups.
  if (user && !user.emailVerified && !verificationSkipped && !isWhitelisted) {
    return (
      <EmailVerificationScreen
        onSkip={() => setVerificationSkipped(true)}
      />
    );
  }

  return (
    <div className="min-h-dvh w-full bg-gradient-to-br from-[#0a0806] via-[#1a1410] to-[#0a0806] text-white">
      <PWAHandler />

      {/* Main content - bottom padding for fixed nav bar (60px matches BottomNavBar height + safe area) */}
      <main
        className="w-full min-h-dvh"
        style={{ paddingBottom: 'calc(60px + env(safe-area-inset-bottom, 8px))' }}
      >
        <SwipeablePages modalsOpen={searchOpen}>
          <Outlet />
        </SwipeablePages>
      </main>

      {/* Bottom Navigation - hidden on paywall, post-entry reflection, and shared entry pages */}
      {location.pathname !== '/paywall' && location.pathname !== '/journal/post-entry' && location.pathname !== '/planner' && !location.pathname.startsWith('/shared/') && <BottomNavBar />}

      {/* Invisible AI Insights - appears automatically */}
      {user && !location.pathname.startsWith('/shared/') && <FloatingInsight />}

      {/* Search Modal */}
      {user && (
        <SearchModal
          isOpen={searchOpen}
          onClose={() => setSearchOpen(false)}
        />
      )}

    </div>
  );
}